import React, { useState } from 'react';

import { BuildMenu } from '../Buildings';
import { Building, BuildingCategory, BuildMenuState } from '../constants';

// Building state interface
export interface BuildingState {
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;
}

// Building actions interface
export interface BuildingActions {
  selectBuilding: (building: Building) => void;
  selectCategory: (category: BuildingCategory | null) => void;
  toggleBuildMenu: () => void;
  closeBuildMenu: () => void;
  exitBuildMode: () => void;
  goBackToCategories: () => void;
  clearSelectedBuilding: () => void;
}

// Buildings component factory type
export type BuildingsComponentFactory = () => React.ReactElement;

// Hook return type
export interface UseBuildingsReturn extends BuildingState, BuildingActions {
  BuildingsComponent: BuildingsComponentFactory;
  // Raw setters for compatibility
  setBuildMenuOpen: React.Dispatch<React.SetStateAction<BuildMenuState>>;
  setSelectedCategory: React.Dispatch<
    React.SetStateAction<BuildingCategory | null>
  >;
  setSelectedBuilding: React.Dispatch<React.SetStateAction<Building | null>>;
}

// Hook that manages building status menu state and interactions
export const useBuildings = (): UseBuildingsReturn => {
  // Building status menu state
  const [buildMenuOpen, setBuildMenuOpen] = useState<BuildMenuState>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<BuildingCategory | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(
    null
  );

  // Handle building selection
  const selectBuilding = (building: Building): void => {
    // Normal building selection - enter build mode
    setSelectedBuilding(building);
    setBuildMenuOpen(false); // Close menu after selecting building
  };

  // Handle category selection
  const selectCategory = (category: BuildingCategory | null): void => {
    setSelectedCategory(category);
  };

  // Toggle build menu
  const toggleBuildMenu = (): void => {
    setBuildMenuOpen(!buildMenuOpen);
    if (!buildMenuOpen) {
      // Opening menu - reset category
      setSelectedCategory(null);
    }
  };

  // Close build menu
  const closeBuildMenu = (): void => {
    setBuildMenuOpen(false);
    setSelectedCategory(null);
    // Keep selected building when menu is closed
  };

  // Exit build mode but keep menu open
  const exitBuildMode = (): void => {
    setSelectedBuilding(null);
  };

  // Go back to category selection
  const goBackToCategories = (): void => {
    setSelectedCategory(null);
    // Keep selected building when going back to categories
  };

  // Clear selected building
  const clearSelectedBuilding = (): void => {
    setSelectedBuilding(null);
  };

  // Pre-configured BuildMenu component factory
  const BuildingsComponent: BuildingsComponentFactory = () =>
    React.createElement(BuildMenu, {
      buildMenuOpen,
      selectedCategory,
      selectedBuilding,
    });

  return {
    // State
    buildMenuOpen,
    selectedCategory,
    selectedBuilding,

    // Actions
    selectBuilding,
    selectCategory,
    toggleBuildMenu,
    closeBuildMenu,
    exitBuildMode,
    goBackToCategories,
    clearSelectedBuilding,

    // Pre-configured component
    BuildingsComponent,

    // Raw setters (for compatibility with existing code)
    setBuildMenuOpen,
    setSelectedCategory,
    setSelectedBuilding,
  };
};
